import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Typography, Button, Empty } from 'antd';
import { RetweetOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import { AntdTable, Container } from 'components/custom-table/Style';

const { Title, Text } = Typography;

const StyledModal = styled(Modal)`
	.ant-modal-content {
		border-radius: 10px;
	}

	.ant-modal-body {
		padding: 24px 32px;
		max-height: 70vh;
		overflow-y: auto;
	}
`;

const ModalHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 24px;
`;

const HeaderIcon = styled.div`
	width: 40px;
	height: 40px;
	border-radius: 8px;
	background: #eff6ff;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #2563eb;
	font-size: 20px;
`;

const SectionTitle = styled(Title)`
	&& {
		margin: 24px 0 12px;
		font-size: 16px;
		font-weight: 600;
		color: #1f2937;
	}

	&&:first-of-type {
		margin-top: 0;
	}
`;

const FooterRow = styled.div`
	display: flex;
	justify-content: center;
	margin-top: 28px;
`;

const CloseButton = styled(Button)`
	height: 40px;
	padding: 0 40px;
	background-color: #1e3a8a;
	border-color: #1e3a8a;
	color: #fff;
	font-weight: 500;
	border-radius: 6px;

	&:hover,
	&:focus {
		background-color: #1e40af !important;
		border-color: #1e40af !important;
		color: #fff !important;
	}
`;

const IST_OFFSET_MINUTES = 330; // UTC+5:30

const formatDateTime = (value) => {
	if (!value) return '-';
	const utc = moment.utc(value, [moment.ISO_8601, 'YYYY-MM-DD HH:mm:ss'], true);
	if (utc.isValid()) {
		return utc.utcOffset(IST_OFFSET_MINUTES).format('DD-MM-YYYY hh:mm A');
	}
	const local = moment(value, ['DD-MM-YYYY hh:mm a', 'DD-MM-YYYY HH:mm'], true);
	return local.isValid() ? local.format('DD-MM-YYYY hh:mm A') : value;
};

const formatCurrency = (value) => {
	if (value == null || value === '' || value === '-') return '-';
	const num = Number(value);
	if (Number.isNaN(num)) return value;
	return `₹ ${num.toLocaleString('en-IN')}`;
};

const renderValue = (value) => (value == null || value === '' ? '-' : value);

const normaliseBasis = (rateBasis) =>
	String(rateBasis || 'flat')
		.toLowerCase()
		.replace(/\s+/g, '_');
const isFlatBasis = (rateBasis) => normaliseBasis(rateBasis) === 'flat';
const isPerOrderBasis = (rateBasis) => normaliseBasis(rateBasis) === 'per_order';

// Per Order: one line per rate_id, e.g. "R1: ₹ 10". `key` is old_rate / new_rate.
const renderRateLines = (row, key) => {
	const lines = row?.order_rate_override?.rate_lines || [];
	if (!lines.length) return '-';
	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
			{lines.map((line) => (
				<span key={line.rate_id}>
					{line.rate_id}: {formatCurrency(line[key])}
				</span>
			))}
		</div>
	);
};

const renderOldPrice = (_, row) => {
	if (isPerOrderBasis(row.rate_basis)) return renderRateLines(row, 'old_rate');
	return formatCurrency(
		isFlatBasis(row.rate_basis)
			? row.old_vendor_rate ?? row.old_price_value
			: row.old_charges_per_km ?? row.old_price_value
	);
};

const renderNewPrice = (_, row) => {
	if (isPerOrderBasis(row.rate_basis)) return renderRateLines(row, 'new_rate');
	return formatCurrency(
		isFlatBasis(row.rate_basis)
			? row.new_vendor_rate ?? row.new_price_value
			: row.new_charges_per_km ?? row.new_price_value
	);
};
const buildVehicleColumns = (rateBasis) => {
	const flat = isFlatBasis(rateBasis);
	const perOrder = isPerOrderBasis(rateBasis);
	let oldRateTitle = 'Old Charges per KM';
	let newRateTitle = 'New Charges per KM';
	if (flat) {
		oldRateTitle = 'Old Vendor Rate';
		newRateTitle = 'New Vendor Rate';
	} else if (perOrder) {
		oldRateTitle = 'Old Per Order Rate';
		newRateTitle = 'New Per Order Rate';
	}
	return [
		{ title: 'Trip ID', dataIndex: 'trip_code', width: 130, render: renderValue },
		{ title: 'Old Vendor', dataIndex: 'old_vendor', width: 170, render: renderValue },
		{ title: 'New Vendor', dataIndex: 'new_vendor', width: 170, render: renderValue },
		{
			title: 'Old Vehicle Type',
			dataIndex: 'old_vehicle_type',
			width: 190,
			render: renderValue,
		},
		{
			title: 'New Vehicle Type',
			dataIndex: 'new_vehicle_type',
			width: 190,
			render: renderValue,
		},
		{ title: 'Old Driver', dataIndex: 'old_driver', width: 160, render: renderValue },
		{ title: 'New Driver', dataIndex: 'new_driver', width: 160, render: renderValue },
		{
			title: 'Old Vehicle Number',
			dataIndex: 'old_vehicle_number',
			width: 170,
			render: renderValue,
		},
		{
			title: 'New Vehicle Number',
			dataIndex: 'new_vehicle_number',
			width: 170,
			render: renderValue,
		},
		{
			title: oldRateTitle,
			dataIndex: 'old_price_value',
			width: perOrder ? 190 : 160,
			render: renderOldPrice,
		},
		{
			title: newRateTitle,
			dataIndex: 'new_price_value',
			width: perOrder ? 190 : 160,
			render: renderNewPrice,
		},
		{ title: 'Deduction', dataIndex: 'deduction_amount', width: 140, render: formatCurrency },
		{ title: 'Updated By', dataIndex: 'updated_by', width: 160, render: renderValue },
		{ title: 'Updated At', dataIndex: 'updated_at', width: 180, render: formatDateTime },
		{ title: 'Reason', dataIndex: 'reason', width: 240, render: renderValue },
	];
};

const DRIVER_COLUMNS = [
	{
		title: 'Old Vendor',
		dataIndex: 'vendor',
		key: 'old_vendor',
		width: 200,
		render: renderValue,
	},
	{
		title: 'New Vendor',
		dataIndex: 'vendor',
		key: 'new_vendor',
		width: 200,
		render: renderValue,
	},
	{ title: 'Initial', dataIndex: 'old_driver', width: 200, render: renderValue },
	{ title: 'Updated', dataIndex: 'new_driver', width: 200, render: renderValue },
	{ title: 'Updated By', dataIndex: 'updated_by', width: 180, render: renderValue },
	{ title: 'Updated At', dataIndex: 'updated_at', width: 200, render: formatDateTime },
	{ title: 'Reason', dataIndex: 'reason', width: 260, render: renderValue },
];

const sumWidth = (cols) => cols.reduce((total, col) => total + (col.width || 150), 0);

const withRowKeys = (rows = []) => rows.map((row, index) => ({ key: row.id ?? index, ...row }));

function ReplacementHistoryModal({
	visible,
	tripCode,
	vehicleReplacements,
	driverReplacements,
	onClose,
}) {
	const vehicleColumns = buildVehicleColumns(vehicleReplacements?.[0]?.rate_basis || 'flat');
	return (
		<StyledModal
			open={visible}
			onCancel={onClose}
			footer={null}
			width={1280}
			destroyOnClose
			closable
		>
			<ModalHeader>
				<HeaderIcon>
					<RetweetOutlined />
				</HeaderIcon>
				<div>
					<Title level={4} style={{ margin: 0, color: '#1f2937' }}>
						Replacement History
					</Title>
					{tripCode ? (
						<Text type="secondary" style={{ fontSize: 13 }}>
							{tripCode}
						</Text>
					) : null}
				</div>
			</ModalHeader>

			<SectionTitle level={5}>Vehicle Replacement</SectionTitle>
			<Container>
				<AntdTable
					columns={vehicleColumns}
					dataSource={withRowKeys(vehicleReplacements)}
					pagination={false}
					size="middle"
					scroll={{ x: sumWidth(vehicleColumns), y: 400 }}
					locale={{
						emptyText: <Empty description="No vehicle replacements" />,
					}}
				/>
			</Container>

			<SectionTitle level={5}>Driver Replacement</SectionTitle>
			<Container>
				<AntdTable
					columns={DRIVER_COLUMNS}
					dataSource={withRowKeys(driverReplacements)}
					pagination={false}
					size="middle"
					scroll={{ x: sumWidth(DRIVER_COLUMNS), y: 400 }}
					locale={{
						emptyText: <Empty description="No driver replacements" />,
					}}
				/>
			</Container>

			<FooterRow>
				<CloseButton onClick={onClose}>Close</CloseButton>
			</FooterRow>
		</StyledModal>
	);
}

ReplacementHistoryModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	tripCode: PropTypes.string,
	vehicleReplacements: PropTypes.arrayOf(PropTypes.object),
	driverReplacements: PropTypes.arrayOf(PropTypes.object),
	onClose: PropTypes.func.isRequired,
};

ReplacementHistoryModal.defaultProps = {
	tripCode: '',
	vehicleReplacements: [],
	driverReplacements: [],
};

export default ReplacementHistoryModal;
